// Wardrobe ownership + equipped state, persisted in localStorage. Buying spends
// Bones (and checks the streak gate); equipping is free. One item per slot.

import { WARDROBE_CATALOG, CATALOG_BY_ID, currentSeasonId, type WardrobeSlot } from "./catalog"
import { spendBones } from "./bones-store"

const OWNED_KEY = "pawket.wardrobe.owned"
const EQUIPPED_KEY = "pawket.wardrobe.equipped"
const SEEN_KEY = "pawket.wardrobe.seenCount"
export const WARDROBE_EVENT = "pawket:wardrobe-changed"

export interface Equipped { look: string | null; body: string | null; hat: string | null; neck: string | null; accessory: string | null; effect: string | null }
export const EMPTY_EQUIPPED: Equipped = { look: null, body: null, hat: null, neck: null, accessory: null, effect: null }

// Free items (price 0) are owned from the start so the equip UI has something to use.
const DEFAULT_OWNED = WARDROBE_CATALOG.filter((i) => i.priceBones === 0).map((i) => i.id)

function emit(): void {
  try { window.dispatchEvent(new CustomEvent(WARDROBE_EVENT)) } catch { /* no window */ }
}
function read<T>(key: string, fallback: T): T {
  try { const r = localStorage.getItem(key); return r ? (JSON.parse(r) as T) : fallback } catch { return fallback }
}
function write(key: string, v: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(v)) } catch { /* quota / no window */ }
}

export function getOwned(): string[] {
  const stored = read<string[]>(OWNED_KEY, [])
  return Array.from(new Set([...DEFAULT_OWNED, ...(Array.isArray(stored) ? stored : [])]))
}
export function isOwned(id: string): boolean { return getOwned().includes(id) }
export function getEquipped(): Equipped {
  return { ...EMPTY_EQUIPPED, ...read<Partial<Equipped>>(EQUIPPED_KEY, {}) }
}

export interface BuyResult { ok: boolean; reason?: string }

export function buyItem(id: string, longestStreak: number): BuyResult {
  const item = CATALOG_BY_ID[id]
  if (!item) return { ok: false, reason: "Unknown item" }
  if (isOwned(id)) return { ok: true }
  // Seasonal drops are only buyable during their quarter — keeps the drop special.
  // (Owned items stay wearable year-round; this only gates first purchase.)
  if (item.seasonal && item.seasonal !== currentSeasonId()) {
    return { ok: false, reason: "That drop is out of season — it'll be back" }
  }
  if (item.minStreak && longestStreak < item.minStreak) {
    return { ok: false, reason: `Reach a ${item.minStreak}-day streak first` }
  }
  if (!spendBones(item.priceBones)) return { ok: false, reason: "Not enough Bones yet" }
  const owned = read<string[]>(OWNED_KEY, [])
  owned.push(id)
  write(OWNED_KEY, owned)
  emit()
  return { ok: true }
}

export function equipItem(id: string): void {
  const item = CATALOG_BY_ID[id]
  if (!item || !isOwned(id)) return
  const eq = getEquipped()
  // Toggle off if already equipped in that slot, else equip.
  eq[item.slot] = eq[item.slot] === id ? null : id
  write(EQUIPPED_KEY, eq)
  emit()
}

export function unequipSlot(slot: WardrobeSlot): void {
  const eq = getEquipped()
  eq[slot] = null
  write(EQUIPPED_KEY, eq)
  emit()
}

/** Strip every slot — the "take it all off" / reset-look action. */
export function unequipAll(): void {
  write(EQUIPPED_KEY, { ...EMPTY_EQUIPPED })
  emit()
}

/** Overwrite owned + equipped from a reconciled remote snapshot, then notify.
 * Used by wardrobe-sync.ts; localStorage stays the source of truth, this just
 * reconciles it with Supabase. */
export function applyRemoteWardrobe(input: { owned: string[]; equipped: Partial<Equipped> }): void {
  const owned = Array.isArray(input.owned)
    ? Array.from(new Set(input.owned.filter((x) => typeof x === "string")))
    : []
  write(OWNED_KEY, owned)
  write(EQUIPPED_KEY, { ...EMPTY_EQUIPPED, ...(input.equipped || {}) })
  emit()
}

/** True while the shop has items the user hasn't browsed since they last opened
 * it — drives the "New" dot on the dashboard so fresh drops get discovered. */
export function hasUnseenItems(): boolean {
  return read<number>(SEEN_KEY, 0) < WARDROBE_CATALOG.length
}

/** Mark the current catalog as seen (called when the wardrobe opens). */
export function markWardrobeSeen(): void {
  write(SEEN_KEY, WARDROBE_CATALOG.length)
  emit()
}
